import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";

function PwInput({ id, value, onChange, testId, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        data-testid={testId}
      />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function GantiPassword() {
  const { logout } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 6) return setError("Password baru minimal 6 karakter.");
    if (newPw !== confirmPw) return setError("Konfirmasi password tidak cocok.");
    if (newPw === oldPw) return setError("Password baru harus berbeda dari password lama.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password berhasil diubah. Silakan masuk kembali.");
      setTimeout(() => logout(), 1200);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Gagal mengubah password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ganti-password-page">
      <PageHeader icon={KeyRound} title="Ganti Password" subtitle="Amankan akun admin Anda dengan password yang kuat dan tidak mudah ditebak." />

      <div className="max-w-lg">
        <Alert className="mb-6 border-brand-sky/60 bg-brand-mist">
          <ShieldCheck className="h-4 w-4 text-brand-teal" />
          <AlertTitle className="text-brand-deep">Tips keamanan</AlertTitle>
          <AlertDescription className="text-brand-deep">
            Gunakan minimal 6 karakter, kombinasikan huruf, angka, dan simbol. Setelah diubah, Anda akan diminta masuk kembali.
          </AlertDescription>
        </Alert>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-lg">Ubah Password Admin</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="old">Password Lama</Label>
                <PwInput id="old" value={oldPw} onChange={setOldPw} testId="old-password-input" placeholder="Password saat ini" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new">Password Baru</Label>
                <PwInput id="new" value={newPw} onChange={setNewPw} testId="new-password-input" placeholder="Minimal 6 karakter" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Konfirmasi Password Baru</Label>
                <PwInput id="confirm" value={confirmPw} onChange={setConfirmPw} testId="confirm-password-input" placeholder="Ulangi password baru" />
              </div>

              {error && (
                <div data-testid="ganti-password-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <Button type="submit" disabled={loading} data-testid="change-password-submit" className="w-full h-11">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Simpan Password Baru
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
